import { CSSProperties } from "react";

import { Check } from "lucide-react";
import { Link } from "react-router";

import { ArticleReadStatusReadStatus } from "@/graphql";

import { CARD_IMG_HEIGHT, CARD_IMG_HEIGHT_XS } from "./constants";
import { getRandomDefaultImg } from "./image-util";

interface ArticleStatusLineProps {
  readStatus: ArticleReadStatusReadStatus | undefined;
  timeToRead?: number;
}

function ArticleStatusLine({
  readStatus,
  timeToRead = 0,
}: ArticleStatusLineProps) {
  if (readStatus === ArticleReadStatusReadStatus.ReadingInProgress) {
    return <div className="text-sm text-gray-500">Started</div>;
  }

  if (readStatus === ArticleReadStatusReadStatus.ReadingCompleted) {
    return (
      <div className="flex items-center text-sm font-medium text-green-600">
        <Check />
        <span className="ml-1">Read</span>
      </div>
    );
  }

  return <div className="text-sm text-gray-500">{timeToRead} min read</div>;
}

interface ArticleCardProps {
  item: {
    id: string;
    title?: string | null;
    timeToRead?: number | null;
    urlSlug?: string | null;
    public?: boolean | null;
    heroImage?: string | null;
    currentUserReadStatus?: ArticleReadStatusReadStatus;
  };
  isXs?: boolean;
  style?: CSSProperties;
  imgCss?: CSSProperties;
}

export function ArticleCard({
  item,
  isXs = false,
  style = {},
  imgCss,
}: ArticleCardProps) {
  const { title, urlSlug, heroImage, currentUserReadStatus, timeToRead } = item;

  // Use actual dimensions - cards are 526x296 aspect ratio (16:9)
  const imgHeight = isXs ? CARD_IMG_HEIGHT_XS : CARD_IMG_HEIGHT;
  const imgWidth = Math.round(imgHeight * (526 / 296)); // Maintain aspect ratio

  // Optimize Contentful parameters: width, height, quality, format
  const imgUrl = heroImage
    ? `${heroImage}?w=${imgWidth * 2}&h=${imgHeight * 2}&q=85&fm=webp&fit=fill`
    : getRandomDefaultImg();
  const url = `/learn/${urlSlug}`;
  const isReadCompleted = "READING_COMPLETED" === currentUserReadStatus;

  return (
    <Link
      to={url}
      className="block w-full h-full text-black hover:no-underline"
      style={style}
    >
      <div
        className={`
          w-full h-full flex flex-col rounded-md overflow-hidden
          border border-border hover:border-blue-500 hover:shadow-sm transition-shadow
          ${isReadCompleted ? "bg-gray-50" : "bg-white"}
        `}
      >
        <div
          className={`
            relative overflow-hidden bg-gray-200
            ${isReadCompleted ? "opacity-40" : ""}
          `}
          style={{
            height: imgHeight,
            ...imgCss,
          }}
        >
          <img
            src={imgUrl}
            alt={title || "Article thumbnail"}
            width={imgWidth}
            height={imgHeight}
            loading="lazy"
            className="w-full h-full object-cover text-transparent"
            style={{
              fontSize: 0,
              backgroundColor: "#e5e7eb",
            }}
            onLoad={(e) => {
              e.currentTarget.style.fontSize = "";
            }}
          />
        </div>
        <div
          className={`
            flex-1 flex flex-col justify-between p-3
            ${isReadCompleted ? "bg-gray-50" : "bg-white"}
          `}
        >
          <h3
            className={`
              !text-base font-semibold
              ${isReadCompleted ? "text-gray-500" : "text-gray-900"}
            `}
          >
            {title}
          </h3>
          <div className="py-2">
            <ArticleStatusLine
              readStatus={currentUserReadStatus}
              timeToRead={timeToRead ?? 0}
            />
          </div>
        </div>
      </div>
    </Link>
  );
}
